<template>
  <el-drawer :visible.sync="visible" size="50%" title="Notificaciones">
    <div class="drawer-content notifications-grid">
      <kbo-table
        :columns="voiceNotificationsColumns"
        :items="preparedVoiceNotifications"
        cache-key="notifications-voice"
        class="voice-table"
        row-key-field="inserted"
        title="Paso por voz"
        @rowClicked="handleNotificationClick"
      />
      <kbo-table
        :columns="noClientNotificationsColumns"
        :items="preparedNoClientNotifications"
        cache-key="notifications-no-client"
        class="no-client-table"
        row-key-field="inserted"
        title="Ausencia"
        @rowClicked="handleNotificationClick"
      >
        <template slot="column_actions" slot-scope="{ item }">
          <span
            class="material-icons m-icon-m success"
            role="button"
            @click.prevent.stop="acceptNoClient(item)"
          >
            check_circle
          </span>
          <span
            class="material-icons m-icon-m danger"
            role="button"
            @click.prevent.stop="rejectNoClient(item)"
          >
            cancel
          </span>
        </template>
      </kbo-table>
      <kbo-table
        :columns="giveBackNotificationsColumns"
        :items="preparedGiveBackNotifications"
        cache-key="notifications-give-back"
        class="give-back-table"
        row-key-field="inserted"
        title="Devolución"
        @rowClicked="handleNotificationClick"
      >
        <template slot="column_actions" slot-scope="{ item }">
          <accept-give-back-button
            container-class="notification-drawer--give-back-button"
            :notification="item"
            :store="$store"
            :on-close="refreshNotifications"
          >
            <span class="material-icons m-icon-m success" @click.prevent.stop="">check_circle</span>
          </accept-give-back-button>
          <span
            class="material-icons m-icon-m danger"
            role="button"
            @click.prevent.stop="rejectGiveBack(item)"
          >
            cancel
          </span>
        </template>
      </kbo-table>
      <kbo-table
        :columns="alarmNotificationsColumns"
        :items="preparedAlarmNotifications"
        :table-style="{ backgroundColor: '#F22' }"
        cache-key="notifications-alarm"
        class="alarms-table"
        row-key-field="inserted"
        title="Petición de alarma"
        @rowClicked="handleNotificationClick"
      />
      <kbo-table
        :columns="servicesNotDeliveredColumns"
        :items="servicesNotDeliveredList"
        cache-key="notifications-services-not-delivered"
        class="errors-table"
        row-key-field="token"
        title="Servicios no enviados"
        @rowClicked="handleServiceErrorClick"
      />
      <kbo-table
        :columns="bookingsNotDeliveredColumns"
        :items="bookingsNotDeliveredList"
        cache-key="notifications-bookings-not-delivered"
        class="errors-table"
        row-key-field="token"
        title="Reservas no enviadas"
        @rowClicked="handleBookingErrorClick"
      />
    </div>
  </el-drawer>
</template>

<script>
import moment from "@/utils/moment";
import { mapActions, mapGetters } from "vuex";
import { RECOVERY_NOT_DELIVERED_BOOKING, RECOVERY_NOT_DELIVERED_SERVICE } from "@/events/constants";
import notifications from "@/mixins/notifications";
import AcceptGiveBackButton from "@/components/common/AcceptGiveBackButton.vue";
import KboTable from "@/components/common/KboTable.vue";

const defaultNotificationColumns = [
  {
    width: "60px",
    label: "Taxi",
    key: "license",
    style: {
      fontWeight: "bold",
    },
  },
  {
    width: "1fr",
    label: "Desde",
    key: "inserted",
    renderer(inserted) {
      return moment()(inserted).fromNow();
    },
  },
];

const defaultNotificationColumnsWithActionsAndAddress = [
  {
    width: "60px",
    label: "Taxi",
    key: "license",
    style: {
      fontWeight: "bold",
    },
  },
  {
    width: "1fr",
    label: "Dirección",
    key: "streetFromText",
    renderer(_, notification) {
      try {
        return JSON.parse(notification.params).streetFromText;
      } catch (e) {
        return "N/A";
      }
    },
  },
  {
    width: "90px",
    label: "Desde",
    key: "inserted",
    renderer(inserted) {
      return moment()(inserted).fromNow();
    },
  },
  {
    width: "80px",
    label: "Acciones",
    key: "actions",
    style: {
      justifyContent: "flex-end",
    },
  },
];
const alarmNotificationsColumns = [...defaultNotificationColumns];
const voiceNotificationsColumns = [...defaultNotificationColumns];
const noClientNotificationsColumns = [...defaultNotificationColumnsWithActionsAndAddress];
const giveBackNotificationsColumns = [...defaultNotificationColumnsWithActionsAndAddress];

const servicesNotDeliveredColumns = [
  {
    width: "100px",
    label: "Teléfono",
    key: "payload.rawData.customerData.phone",
    style: {
      fontWeight: "bold",
    },
  },
  {
    width: "2fr",
    label: "Desde",
    key: "inserted",
    renderer(inserted) {
      return moment()(inserted * 1000).fromNow();
    },
  },
];

const bookingsNotDeliveredColumns = [
  {
    width: "100px",
    label: "Teléfono",
    key: "payload.customer.phone",
    style: {
      fontWeight: "bold",
    },
  },
  {
    width: "2fr",
    label: "Desde",
    key: "inserted",
    renderer(inserted) {
      return moment()(inserted * 1000).fromNow();
    },
  },
];

export default {
  components: {
    AcceptGiveBackButton,
    KboTable,
  },
  mixins: [notifications],
  computed: {
    ...mapGetters("ui", ["isNotificationsDrawerShown"]),
    ...mapGetters("app", ["taxis"]),
    ...mapGetters("clientNotifications", [
      "alarmNotifications",
      "voiceNotifications",
      "noClientNotifications",
      "giveBackNotifications",
    ]),
    ...mapGetters("services", ["servicesNotDelivered", "bookingsNotDelivered"]),
    servicesNotDeliveredList() {
      return Object.values(this.servicesNotDelivered);
    },
    bookingsNotDeliveredList() {
      return Object.values(this.bookingsNotDelivered);
    },
    preparedAlarmNotifications() {
      return this.prepareNotifications(this.alarmNotifications);
    },
    preparedVoiceNotifications() {
      return this.prepareNotifications(this.voiceNotifications);
    },
    preparedNoClientNotifications() {
      return this.prepareNotifications(this.noClientNotifications);
    },
    preparedGiveBackNotifications() {
      return this.prepareNotifications(this.giveBackNotifications);
    },
    visible: {
      set(v) {
        (v && this.showNotificationsDrawer()) || this.hideNotificationsDrawer();
      },
      get() {
        return this.isNotificationsDrawerShown;
      },
    },
  },
  data() {
    return {
      alarmNotificationsColumns,
      voiceNotificationsColumns,
      noClientNotificationsColumns,
      giveBackNotificationsColumns,
      servicesNotDeliveredColumns,
      bookingsNotDeliveredColumns,
    };
  },
  methods: {
    ...mapActions("ui", ["showNotificationsDrawer", "hideNotificationsDrawer"]),
    prepareNotifications(notifications) {
      return notifications.map((n) => ({
        ...n,
        license: this.taxis.find((t) => t.id === n.idTaxi && t.idWorkgroup === n.idWg)?.license,
      }));
    },
    handleServiceErrorClick(notDeliveredService) {
      this.$bus.emit(RECOVERY_NOT_DELIVERED_SERVICE, notDeliveredService);
      this.close();
    },
    handleBookingErrorClick(notDeliveredBooking) {
      this.$bus.emit(RECOVERY_NOT_DELIVERED_BOOKING, notDeliveredBooking);
      this.close();
    },
    close() {
      this.visible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.notifications-grid {
  display: grid;
  grid-template-rows: 1fr 1fr 1fr;
  grid-template-columns: 1fr 1fr;
  grid-gap: 5px 20px;
  height: 100%;
  padding-bottom: 20px;
}

::v-deep .alarms-table {
  .KBO_Table__title__text {
    color: #222;
  }

  .KBO_Table__header {
    background-color: rgba(200, 0, 0, 0.8);
  }

  .KBO_Table__empty,
  .KBO_Table__scroller {
    background-color: rgba(200, 0, 0, 0.1);
    color: #222;
  }

  .KBO_Table__row {
    border-bottom-color: rgba(200, 0, 0, 0.1);
  }
}

::v-deep .voice-table {
  .KBO_Table__title__text {
    color: #222;
  }

  .KBO_Table__header {
    background-color: rgba(48, 106, 213, 0.8);
  }

  .KBO_Table__empty,
  .KBO_Table__scroller {
    background-color: rgba(48, 106, 213, 0.1);
    color: #222;
  }

  .KBO_Table__row {
    border-bottom-color: rgba(0, 120, 0, 0.1);
  }
}

::v-deep .give-back-table {
  .KBO_Table__title__text {
    color: #222;
  }

  .KBO_Table__header {
    background-color: rgba(255, 165, 0, 0.8);
  }

  .KBO_Table__empty,
  .KBO_Table__scroller {
    background-color: rgba(255, 165, 0, 0.1);
    color: #222;
  }

  .KBO_Table__row {
    border-bottom-color: rgba(255, 165, 0, 0.1);
  }
}

::v-deep .no-client-table {
  .KBO_Table__title__text {
    color: #222;
  }

  .KBO_Table__header {
    background-color: rgba(130, 0, 195, 0.8);
  }

  .KBO_Table__empty,
  .KBO_Table__scroller {
    background-color: rgba(130, 0, 195, 0.1);
    color: #222;
  }

  .KBO_Table__row {
    border-bottom-color: rgba(130, 0, 195, 0.1);
  }
}

::v-deep .errors-table {
  .KBO_Table__title__text {
    color: #222;
  }

  .KBO_Table__header {
    background-color: rgba(215, 129, 255, 0.8);
  }

  .KBO_Table__empty,
  .KBO_Table__scroller {
    background-color: rgba(225, 160, 255, 0.1);
    color: #222;
  }

  .KBO_Table__row {
    border-bottom-color: rgba(0, 0, 0, 0.1);
  }
}

.success {
  color: $green;
}

.danger {
  color: $red;
}

[role="button"] {
  cursor: pointer;
}

.notification-drawer--give-back-button {
  height: 24px;
}
</style>
